import { useState } from 'react';
import { Moon, Sun, Palette, Globe } from 'lucide-react';
import { useTheme, accentConfigs, langConfigs } from '@kss/theme';
import { cn } from '@kss/ui';

/**
 * Accent colour + light/dark + language controls. Rendered at the bottom (footer),
 * so the popovers open UPWARD. Buttons are labelled chips for clear visibility.
 */
export function ThemeControls() {
  const { isDark, toggleMode, accent, setAccent, lang, setLang } = useTheme();
  const [accentOpen, setAccentOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const current = langConfigs.find((l) => l.code === lang);

  const chip =
    'flex items-center gap-1.5 rounded-lg border border-border bg-surface-card px-3 py-1.5 text-xs font-semibold text-content-secondary transition-colors hover:border-primary hover:text-primary';

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {/* Accent colour */}
      <div className='relative'>
        <button onClick={() => setAccentOpen((o) => !o)} aria-label='Accent colour' className={chip}>
          <Palette size={14} /> Theme
        </button>
        {accentOpen && (
          <>
            <div className='fixed inset-0 z-40' onClick={() => setAccentOpen(false)} />
            <div className='absolute bottom-full left-0 z-50 mb-2 rounded-lg border border-border bg-surface-card p-2 shadow-pop'>
              <div className='grid grid-cols-4 gap-1.5'>
                {accentConfigs.map((a) => (
                  <button
                    key={a.name}
                    onClick={() => {
                      setAccent(a.name);
                      setAccentOpen(false);
                    }}
                    aria-label={a.label}
                    className={cn('h-6 w-6 rounded-full ring-offset-1', accent === a.name && 'ring-2 ring-content-primary')}
                    style={{ background: a.previewColor }}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Light / dark */}
      <button onClick={toggleMode} aria-label='Toggle dark mode' className={chip}>
        {isDark ? <Sun size={14} /> : <Moon size={14} />} {isDark ? 'Light' : 'Dark'}
      </button>

      {/* Language */}
      <div className='relative'>
        <button onClick={() => setLangOpen((o) => !o)} aria-label='Language' className={chip}>
          <Globe size={14} /> {current?.label ?? 'Language'}
        </button>
        {langOpen && (
          <>
            <div className='fixed inset-0 z-40' onClick={() => setLangOpen(false)} />
            <div className='absolute bottom-full left-0 z-50 mb-2 w-36 rounded-lg border border-border bg-surface-card p-1 shadow-pop'>
              {langConfigs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => {
                    setLang(l.code);
                    setLangOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[12px] font-semibold',
                    lang === l.code ? 'bg-primary-light text-primary' : 'text-content-secondary hover:bg-surface-card-hover'
                  )}
                >
                  {l.native} <span>{l.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
